import type {NextPage} from 'next';
import Head from 'next/head';
import React from 'react';
import ReactPlayer from 'react-player';
import Web3 from 'web3';

import {
  calculatePriceByTier,
  CannesToken,
  CANNES_DEFAULT_TOKENS,
} from '@/utils/white-rabbit/cannes';
import web3Client from '@/utils/Web3Client';
import {ShibuyaCannesSaleConfig} from '@/utils/types';
import Hero from '@/components/white-rabbit/cannes/Hero';
import {useBreakpoints} from '@/utils/hooks';
import toaster from '@/utils/toaster';
import BackgroundVideo from '@/components/BackgroundVideo';
import FaqSection from '@/components/white-rabbit/cannes/FaqSection';
import FadeIn from '@/components/FadeIn';

const TierCard = ({
  token,
  isSaleOpen,
  onMint,
}: {
  token: CannesToken;
  isSaleOpen: boolean;
  onMint: (token: CannesToken) => Promise<void>;
}) => {
  const {isMobile} = useBreakpoints();
  const {title, description, price, datetime, available, video} = token;
  const eth = Web3.utils.fromWei(price || '0');
  const isSoldOut = available === 0;
  const isDisabled = isSoldOut || !isSaleOpen;

  const handleMintToken = () => onMint(token);

  return (
    <div className="mb-4 flex w-full flex-col rounded border border-stone-800 bg-stone-900 p-4 text-left text-white sm:flex-row">
      <div className="mb-8 flex w-full max-w-full items-center justify-center sm:mb-0 sm:mr-8 sm:w-72">
        {isMobile ? (
          <img src={video.gif} className="block w-full sm:hidden" />
        ) : (
          <ReactPlayer
            className="hidden w-full overflow-hidden sm:block"
            style={{maxWidth: '100%', maxHeight: '100%'}}
            width="auto"
            height="100%"
            url={video.webm}
            controls={false}
            muted
            playing
            loop
          />
        )}
      </div>

      <div className="flex-1">
        <div className="my-4 w-full border-b border-stone-700 pb-4">
          <div className="mb-2 flex flex-row items-center justify-between">
            <h4 className="text-lg font-semibold sm:text-xl">{title}</h4>
            {isSoldOut ? (
              <span className="ml-2 inline-block rounded-full border border-stone-700 bg-stone-800 py-1 px-3 text-xs leading-tight text-stone-500 sm:ml-3">
                Sold out
              </span>
            ) : (
              <span className="ml-2 inline-block rounded-full border border-stone-700 bg-stone-800 py-1 px-3 text-xs leading-tight text-stone-300 sm:ml-3">
                {available}{' '}
                <span className="hidden sm:inline">
                  {available === 1 ? 'pass' : 'passes'}
                </span>{' '}
                remaining
              </span>
            )}
          </div>

          <div className="text-stone-400">{description}</div>
        </div>

        <div className="">
          <div className="mb-8 flex justify-between">
            <div className="mr-4">
              <label className="text-sm font-medium text-stone-400">Date</label>
              <p className="font-bold text-white">{datetime}</p>
            </div>

            <div className="mr-4">
              <label className="text-sm font-medium text-stone-400">
                Price
              </label>
              <p className="whitespace-nowrap font-bold text-gold-500	">
                {eth} ETH
              </p>
            </div>
          </div>

          {isSaleOpen && (
            <div className="flex justify-end">
              <button
                disabled={isDisabled}
                className={
                  isDisabled
                    ? 'cursor-not-allowed rounded border border-stone-500 px-8 py-2 font-semibold text-stone-500 transition-colors hover:border-stone-500 hover:bg-stone-900 hover:text-stone-500'
                    : 'rounded border border-gold-500 px-8 py-2 font-semibold text-gold-500 transition-colors hover:border-gold-500 hover:bg-stone-900 hover:text-gold-500'
                }
                onClick={handleMintToken}
              >
                {isSoldOut ? 'Sold out' : 'Mint'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const TokensList = ({
  className,
  tokens,
  onMintToken,
  isSaleOpen,
}: {
  className?: string;
  tokens: Array<CannesToken>;
  onMintToken: (token: CannesToken) => Promise<void>;
  isSaleOpen: boolean;
}) => {
  return (
    <div className={className}>
      <section className="mb-8">
        <div className="mb-4">
          <div className="py-4">
            <h3 className="text-left text-sm font-medium uppercase leading-loose tracking-widest text-yellow-700">
              Bronze Tier
            </h3>
            <div className="h-px bg-gradient-to-r from-yellow-700"></div>
          </div>
          {tokens
            .filter((t) => t.tier === 'bronze')
            .map((token) => {
              return (
                <TierCard
                  key={token.id}
                  token={token}
                  isSaleOpen={isSaleOpen}
                  onMint={onMintToken}
                />
              );
            })}
        </div>
      </section>

      <section className="mb-8">
        <div className="mb-4">
          <div className="py-4">
            <h3 className="text-left text-sm font-medium uppercase leading-loose tracking-widest text-slate-300">
              Silver Tier
            </h3>
            <div className="h-px bg-gradient-to-r from-slate-300"></div>
          </div>
          {tokens
            .filter((t) => t.tier === 'silver')
            .map((token) => {
              return (
                <TierCard
                  key={token.id}
                  token={token}
                  isSaleOpen={isSaleOpen}
                  onMint={onMintToken}
                />
              );
            })}
        </div>
      </section>

      <section className="mb-8">
        <div className="mb-4">
          <div className="py-4">
            <h3 className="text-left text-sm font-medium uppercase leading-loose tracking-widest text-gold-500">
              Gold Tier
            </h3>
            <div className="h-px bg-gradient-to-r from-gold-500"></div>
          </div>
          {tokens
            .filter((t) => t.tier === 'gold')
            .map((token) => {
              return (
                <TierCard
                  key={token.id}
                  token={token}
                  isSaleOpen={isSaleOpen}
                  onMint={onMintToken}
                />
              );
            })}
        </div>
      </section>
    </div>
  );
};

const RegistrationStateNotice = ({
  email,
  nfts,
  onVerifyWallet,
  onEditEmail,
}: {
  email: string | null;
  nfts: Array<any>;
  onVerifyWallet: () => void;
  onEditEmail?: () => void;
}) => {
  if (!nfts || !nfts.length) {
    return null;
  }

  if (email && email.length > 1) {
    return (
      <FadeIn>
        <div className="mb-8 flex flex-col items-center justify-between rounded border border-gold-500 bg-gold-500 bg-opacity-40 p-4 sm:flex-row">
          <div className="flex-1">
            <h4 className="mb-2 inline-flex items-center text-lg font-semibold">
              <span>You have successfully registered!</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="ml-2 h-5 w-5 text-gold-500"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            </h4>

            <p className="text-sm text-stone-300">
              <span className="font-medium text-white">{email}</span> has been
              registered for{' '}
              <span className="font-medium text-white">
                {nfts.length} producer {nfts.length === 1 ? 'pass' : 'passes'}
              </span>
              .
            </p>
          </div>

          <button
            className="rounded-lg border border-gold-500 bg-gold-500 py-2 px-4 text-sm font-medium text-stone-50 transition-colors hover:text-white"
            onClick={onEditEmail}
          >
            Change your email
          </button>
        </div>
      </FadeIn>
    );
  }

  return (
    <FadeIn>
      <div className="mb-8 flex flex-col items-center justify-between rounded border border-gold-500 bg-gold-500 bg-opacity-40 p-4 sm:flex-row">
        <div className="mb-4 flex-1 sm:mb-0">
          <h4 className="mb-2 inline-flex items-center text-base font-semibold sm:text-lg">
            <span>It looks like you have some passes in your wallet!</span>
          </h4>

          <p className="text-sm text-stone-300">
            Verify your identity in order to check the status of your
            registration.
          </p>
        </div>

        <button
          className="w-full rounded-lg border border-gold-500 bg-gold-500 py-2 px-4 text-sm font-medium text-stone-50 transition-colors hover:text-white sm:w-auto"
          onClick={onVerifyWallet}
        >
          Verify your identity
        </button>
      </div>
    </FadeIn>
  );
};

export const CannesBrutPage: NextPage = () => {
  const [isLoading, setLoadingState] = React.useState(true);
  const [userMerkleProof, setUserMerkleProof] = React.useState<string[] | null>(
    null
  );
  const [modal, setModalState] = React.useState<
    null | 'mint' | 'review' | 'edit'
  >(null);
  const [selected, setSelectedToken] = React.useState<CannesToken>(
    CANNES_DEFAULT_TOKENS[0]
  );
  const [config, setSaleConfig] = React.useState<ShibuyaCannesSaleConfig>({
    goldTierPrice: '7000000000000000000',
    silverTierPrice: '6000000000000000000',
    bronzeTierPrice: '5000000000000000000',
    maxPerTransaction: 2,
    maxSupply: 75,
    saleOpen: false,
  });
  const [numMintedPerTier, setNumMintedPerTier] = React.useState<Array<number>>(
    [0, 0, 0, 0, 0, 0]
  );
  const [maxSupplyPerTier, setMaxSupplyPerTier] = React.useState<Array<number>>(
    [7, 6, 4, 4, 4, 50]
  );
  const [nfts, setMintedNfts] = React.useState<Array<any>>([]);
  const [email, setVerifiedEmail] = React.useState<string | null>(null);

  React.useEffect(() => {
    const init = async () => {
      try {
        const [config, numMintedPerTier, maxSupplyPerTier] = await Promise.all([
          web3Client.getShibuyaCannesSaleConfig(),
          web3Client.getNumberMintedForTiers(),
          web3Client.getMaxSupplyForTiers(),
        ]);

        setSaleConfig(config);
        setNumMintedPerTier(numMintedPerTier);
        setMaxSupplyPerTier(maxSupplyPerTier);
      } catch (err: any) {
        console.error('Failed to load contract data:', err);
        // TODO: handle errors better!
      } finally {
        setLoadingState(false);
      }
    };

    init();
  }, []);

  const handleMintToken = async (token: CannesToken) => {
    // TODO: for now we just try to connect the wallet when the user tries to mint
    // if we see there is no connected address, but it might be smoother to handle
    // this in the modal flow (e.g. a "connect your wallet modal" pops up first)
    setSelectedToken(token);
    setModalState('mint');
  };

  const handleMintSuccess = async () => {
    try {
      const [config, numMintedPerTier, maxSupplyPerTier] = await Promise.all([
        web3Client.getShibuyaCannesSaleConfig(),
        web3Client.getNumberMintedForTiers(),
        web3Client.getMaxSupplyForTiers(),
      ]);

      setSaleConfig(config);
      setNumMintedPerTier(numMintedPerTier);
      setMaxSupplyPerTier(maxSupplyPerTier);
    } catch (err: any) {
      console.error('Failed to load contract data:', err);
      toaster.error(
        err.message || String(err) || 'Failed to load contract data!'
      );
    }
  };

  const handleVerifyEmail = async (email: string) => {
    try {
      setVerifiedEmail(email);
    } catch (err: any) {
      console.error('Failed to load NFTs:', err);
      toaster.error(err.message || String(err) || 'Failed to load NFTs!');
    }
  };

  const handleVerifyWallet = () => setModalState('review');

  const handleCloseModal = () => setModalState(null);

  const tokens = CANNES_DEFAULT_TOKENS.map((token, index) => {
    const {tier} = token;
    const minted = numMintedPerTier[index];
    const supply = maxSupplyPerTier[index];
    const available = Math.max(supply - minted, 0);

    return {
      ...token,
      supply,
      available,
      price: calculatePriceByTier(config, tier),
    };
  });

  const isSoldOut = tokens.every(({available}) => available === 0);
  const maxQuantityToMint =
    selected.available >= config.maxPerTransaction
      ? config.maxPerTransaction
      : selected.available;
  const isSaleOpen = config.saleOpen || !!userMerkleProof;

  return (
    <div className="flex min-h-full flex-auto flex-col font-poppins">
      <BackgroundVideo src="/videos/cannes-bg-animation.mp4" />

      <Head>
        <title>Shibuya x Cannes x Brut</title>
        <meta
          property="og:title"
          content="Shibuya x Cannes x Brut"
          key="title"
        />
        <meta
          name="og:description"
          key="description"
          content="Brut, official media partner of the Cannes Film Festival, has partnered with Web3 artist pplpleasr in an effort to make the film industry more inclusive to young female creators."
        />
        <meta
          property="og:image"
          content="https://shibuya.film/images/cannes.jpg"
          key="og-image"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@shibuyafilm" />
        <meta name="twitter:title" content="Shibuya x Cannes x Brut" />
        <meta
          name="twitter:description"
          content="Brut, official media partner of the Cannes Film Festival, has partnered with Web3 artist pplpleasr in an effort to make the film industry more inclusive to young female creators."
        />
        <meta
          name="twitter:image"
          content="https://shibuya.film/images/cannes.jpg"
        />
      </Head>

      <main className="relative flex-1 py-12 text-white">
        <div className="mx-auto w-full max-w-4xl py-4 px-6">
          <Hero
            className="mb-24"
            isSaleOpen={isSaleOpen}
            isSoldOut={isSoldOut}
          />

          {isLoading ? (
            <div>{/* TODO: show loading indicator? */}</div>
          ) : (
            <TokensList
              tokens={tokens}
              onMintToken={handleMintToken}
              isSaleOpen={isSaleOpen}
            />
          )}

          <FaqSection />
        </div>
      </main>
    </div>
  );
};

export default CannesBrutPage;
